import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  SelectInput,
  string,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminIraCompanionsQueryKey,
  useGetAdminIraCompanionAvailablePlans,
  useGetAdminPlanAdvisors,
  useGetAdminRolloverProviders,
  usePostAdminIraCompanion
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { errorMessage } from '../helpers';

type FormProps = {
  advisorId: string;
  investmentVehicleId: string;
  rolloverProviderId: string;
};

const resolver = zodResolver(
  z.object({
    advisorId: string(),
    investmentVehicleId: string({ required: true }),
    rolloverProviderId: string({ required: true })
  })
);

export const IraCompanionFormDialog: FC<{ onClose: () => void }> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormProps>({
    defaultValues: {
      advisorId: '',
      investmentVehicleId: '',
      rolloverProviderId: ''
    },
    mode: 'all',
    resolver
  });

  const investmentVehicleId = methods.watch('investmentVehicleId');

  const rolloverProviders = useGetAdminRolloverProviders();

  const availablePlans = useGetAdminIraCompanionAvailablePlans();

  const planId = availablePlans.data?.find(
    plan => plan.investmentVehicleId === investmentVehicleId
  )?.planId;

  const advisors = useGetAdminPlanAdvisors(planId as string, {
    query: { enabled: !!planId }
  });

  const createIraCompanion = usePostAdminIraCompanion({
    mutation: {
      onError: error =>
        notifications.error(
          errorMessage(error, 'Failed to create IRA companion')
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminIraCompanionsQueryKey()
        });
        notifications.success('IRA companion created');
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormProps) =>
    createIraCompanion.mutate({
      data: {
        investmentVehicleId: data.investmentVehicleId,
        rolloverProviderId: data.rolloverProviderId,
        ...(data.advisorId && { advisorId: data.advisorId })
      }
    });

  useEffect(() => {
    methods.setValue('advisorId', '');
  }, [investmentVehicleId, methods]);

  return (
    <Dialog
      data-testid='iraCompanionFormDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Create IRA Companion</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <SelectInput
                fullWidth
                label='Rollover Provider'
                name='rolloverProviderId'
                options={(rolloverProviders.data ?? []).map(provider => ({
                  label: provider.name ?? '',
                  value: provider.rolloverProviderId
                }))}
                placeholder='Select Rollover Provider'
                required
                size='small'
              />
            </Grid>
            <Grid size={12}>
              <SelectInput
                fullWidth
                label='Plan'
                name='investmentVehicleId'
                options={(availablePlans.data ?? []).map(plan => ({
                  label: plan.contractNumber
                    ? `${plan.planName} (${plan.contractNumber})`
                    : plan.planName,
                  value: plan.investmentVehicleId
                }))}
                placeholder='Select Plan'
                required
                size='small'
              />
            </Grid>
            <Grid size={12}>
              <SelectInput
                disabled={!planId}
                fullWidth
                label='Advisor'
                name='advisorId'
                options={(advisors.data ?? []).map(advisor => ({
                  label: `${advisor.firstName} ${advisor.lastName}`,
                  value: advisor.advisorId
                }))}
                placeholder='Select Advisor'
                size='small'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelIraCompanion'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='saveIraCompanion'
            disabled={createIraCompanion.isPending}
            triggerValidation
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

IraCompanionFormDialog.displayName = 'IraCompanionFormDialog';
